
import React from "react";
import { getExpiryStatusText, getDaysUntilExpiry, getExpiryStatusColor } from "./types";
import { Contract } from "@/lib/types";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format } from "date-fns";

interface ExpiryStickerProps {
  contract: Contract;
}

export function ExpirySticker({ contract }: ExpiryStickerProps) {
  const statusText = getExpiryStatusText(contract);
  const daysUntilExpiry = getDaysUntilExpiry(contract.expiryDate);
  const isAutoRenewing = contract.type?.auto_renews === true;
  const backgroundColor = getExpiryStatusColor(daysUntilExpiry, isAutoRenewing);
  
  // Determine text color - use dark text on light backgrounds, white text on dark backgrounds
  const isDarkBackground = daysUntilExpiry < 0 && !isAutoRenewing;
  const textColor = isDarkBackground ? "white" : "#333";
  
  // Create a user-friendly tooltip message about the contract expiry
  const getTooltipMessage = () => {
    const expiryDate = new Date(contract.expiryDate);
    const formattedDate = format(expiryDate, "MMMM d, yyyy");
    
    if (isAutoRenewing) {
      return contract.type?.renewal_interval && contract.type?.renewal_unit
        ? `Auto-renews every ${contract.type.renewal_interval} ${contract.type.renewal_unit.toLowerCase()}${contract.type.renewal_interval > 1 ? 's' : ''} after ${formattedDate}`
        : `Auto-renews after ${formattedDate}`;
    }
    
    if (daysUntilExpiry < 0) {
      const daysAgo = Math.abs(daysUntilExpiry);
      return `Expired ${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago on ${formattedDate}`;
    } else if (daysUntilExpiry === 0) {
      return `Expires today (${formattedDate})`;
    } else {
      return `Expires in ${daysUntilExpiry} ${daysUntilExpiry === 1 ? 'day' : 'days'} on ${formattedDate}`;
    }
  };
  
  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <div 
            className="inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-semibold cursor-help"
            style={{ 
              backgroundColor: backgroundColor,
              color: textColor,
              minWidth: "70px",
              textAlign: "center"
            }}
          >
            {statusText}
            {!isAutoRenewing && daysUntilExpiry >= 0 && daysUntilExpiry <= 30 && (
              <span className="ml-1">
                ({daysUntilExpiry}d)
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-center">
          {getTooltipMessage()}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

